import React, { useState, useEffect } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TaskTemplateCard } from '@components/molecules/TaskTemplateCard';
import { useTaskTemplates, useImportTaskTemplate, useDeleteTaskTemplate } from '@hooks/api';
import { TaskTemplate, TaskTemplateData } from '@models/taskTemplate.model';
import { Search, ChevronLeft } from 'lucide-react';
import { formatDateForRequest, formatForDisplay, parseDateOnly } from '@utils/formatDate';
import { addWorkingDays, nextWorkingDay } from '@utils/workingDays';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

interface ImportTaskTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectStartDate?: string | Date;
  projectEndDate?: string | Date;
  parentTaskId?: string;
  onSuccess?: () => void;
}

const calculateLastTaskEndDate = (
  importStartDate: string,
  tasks: TaskTemplateData[],
): Date | null => {
  if (!importStartDate || !tasks.length) return null;

  let maxDays = 0;
  tasks.forEach((task) => {
    const taskEnd = (task.daysFromStart || 0) + (task.duration || 0);
    if (taskEnd > maxDays) maxDays = taskEnd;
  });

  return addWorkingDays(nextWorkingDay(parseDateOnly(importStartDate)), maxDays);
};

const calculateTaskDate = (
  importStartDate: string,
  daysFromStart: number | null,
): Date | null => {
  if (!importStartDate || daysFromStart === null || daysFromStart === undefined)
    return null;

  // Anchored on a working day, exactly as the server does when it rebuilds the dates —
  // otherwise this preview shows a date the import will not produce.
  return addWorkingDays(nextWorkingDay(parseDateOnly(importStartDate)), daysFromStart);
};

type Step = 'select' | 'configure' | 'preview';

export const ImportTaskTemplateModal: React.FC<ImportTaskTemplateModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectStartDate,
  projectEndDate,
  parentTaskId,
  onSuccess,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const [step, setStep] = useState<Step>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tasksToImport, setTasksToImport] = useState<TaskTemplateData[]>([]);
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplate | null>(null);
  const [exceedsProjectRange, setExceedsProjectRange] = useState(false);
  const [lastTaskEndDate, setLastTaskEndDate] = useState<Date | null>(null);
  const [adjustEndDates, setAdjustEndDates] = useState(false);

  const { data: templates, isLoading } = useTaskTemplates();
  const importMutation = useImportTaskTemplate();
  const deleteMutation = useDeleteTaskTemplate();

  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedTemplate(null);
      setStartDate(
        projectStartDate
          ? (formatDateForRequest(projectStartDate, true) as string) || ''
          : '',
      );
      setSearchQuery('');
      setTasksToImport([]);
      setAdjustEndDates(false);
    }
  }, [isOpen, projectStartDate]);

  useEffect(() => {
    if (selectedTemplate) {
      setTasksToImport([...selectedTemplate.tasks]);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (selectedTemplate && startDate && projectEndDate) {
      const calculatedEnd = calculateLastTaskEndDate(
        startDate,
        selectedTemplate.tasks,
      );
      setLastTaskEndDate(calculatedEnd);

      if (calculatedEnd) {
        // Parse projectEndDate in local timezone to avoid UTC conversion issues
        const projectEndStr =
          typeof projectEndDate === 'string'
            ? projectEndDate
            : projectEndDate.toISOString().split('T')[0];
        const projectEnd = parseDateOnly(projectEndStr);
        setExceedsProjectRange(calculatedEnd > projectEnd);
      }
    } else {
      setExceedsProjectRange(false);
      setLastTaskEndDate(null);
    }
  }, [selectedTemplate, startDate, projectEndDate]);

  const filteredTemplates = templates?.filter((t) =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleTemplateSelect = (template: TaskTemplate) => {
    setSelectedTemplate(template);
  };

  const handleNext = () => {
    if (step === 'select' && selectedTemplate) {
      setStep('configure');
    } else if (step === 'configure' && startDate) {
      setStep('preview');
    }
  };

  const handleBack = () => {
    if (step === 'configure') {
      setStep('select');
    } else if (step === 'preview') {
      setStep('configure');
    }
  };

  const handleImport = async () => {
    if (!selectedTemplate || !startDate) return;
    // Guard against an empty projectId (the callers pass `id || ''` /
    // `templateProjectId || id || ''`, so a not-yet-loaded route param would
    // otherwise send an empty projectId and fail server-side with no feedback).
    if (!projectId) {
      toast.error(tActionMessages.noProjectSelectedReopen);
      return;
    }

    try {
      await importMutation.mutateAsync({
        templateId: selectedTemplate.templateId,
        projectId,
        startDate,
        projectEndDate: projectEndDate || undefined,
        adjustEndDates,
        parentTaskId,
        tasks: tasksToImport,
      });

      onClose();
      onSuccess?.();
    } catch (error) {
      console.error('Error importing template:', error);
    }
  };

  const handleTaskNameChange = (tempId: string, newName: string) => {
    setTasksToImport((prev) =>
      prev.map((t) => (t.tempId === tempId ? { ...t, name: newName } : t)),
    );
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await deleteMutation.mutateAsync(templateToDelete.templateId);
      if (selectedTemplate?.templateId === templateToDelete.templateId) {
        setSelectedTemplate(null);
      }
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const getTitle = () => {
    switch (step) {
      case 'select':
        return 'Select Task Template';
      case 'configure':
        return 'Configure Import';
      case 'preview':
        return 'Preview Tasks';
      default:
        return 'Import Task Template';
    }
  };

  const getConfirmText = () => {
    switch (step) {
      case 'select':
        return 'Next';
      case 'configure':
        return 'Preview Tasks';
      case 'preview':
        return 'Import Tasks';
      default:
        return 'Next';
    }
  };

  const isConfirmDisabled = () => {
    switch (step) {
      case 'select':
        return !selectedTemplate;
      case 'configure':
        return !startDate;
      case 'preview':
        return false;
      default:
        return true;
    }
  };

  const handleConfirm = () => {
    if (step === 'preview') {
      handleImport();
    } else {
      handleNext();
    }
  };

  const renderSelectStep = () => (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search templates..."
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-gray-500">Loading templates...</div>
      ) : !filteredTemplates?.length ? (
        <div className="py-8 text-center text-gray-500">
          {searchQuery ? 'No templates match your search' : 'No templates available'}
        </div>
      ) : (
        <div className="grid gap-3 max-h-96 overflow-y-auto">
          {filteredTemplates.map((template) => (
            <TaskTemplateCard
              key={template.templateId}
              template={template}
              isSelected={selectedTemplate?.templateId === template.templateId}
              onClick={() => handleTemplateSelect(template)}
              onDelete={() => setTemplateToDelete(template)}
            />
          ))}
        </div>
      )}
    </div>
  );

  const renderConfigureStep = () => (
    <div className="flex flex-col gap-4">
      <div className="p-4 bg-indigo-50 rounded-lg">
        <p className="text-sm text-indigo-800">
          Selected template:{' '}
          <span className="font-medium">{selectedTemplate?.name}</span>
        </p>
        <p className="text-xs text-indigo-600 mt-1">
          {selectedTemplate?.tasks.length} tasks will be imported
        </p>
      </div>

      <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg">
        <p className="text-sm text-gray-700 font-medium">Schedule-Relative Template</p>
        <p className="text-xs text-gray-500 mt-1">
          Task dates will be calculated relative to the start date using business days
          only (weekends excluded).
        </p>
      </div>

      <FormGroup label="Start Date" htmlFor="importStartDate">
        <Input
          id="importStartDate"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Select start date"
        />
        <p className="mt-1 text-xs text-gray-500">
          Task dates will be calculated relative to this start date.
        </p>
      </FormGroup>

      {exceedsProjectRange && lastTaskEndDate && projectEndDate && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 font-medium">
            Some tasks will exceed the project end date
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Tasks would end on{' '}
            <span className="font-medium">{formatForDisplay(lastTaskEndDate)}</span>
            , but the project ends on{' '}
            <span className="font-medium">{formatForDisplay(projectEndDate)}</span>.
          </p>

          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={adjustEndDates}
              onChange={(e) => setAdjustEndDates(e.target.checked)}
              className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm text-amber-800">
              Adjust task end dates to fit within project end date
            </span>
          </label>

          {!adjustEndDates && (
            <p className="text-xs text-amber-600 mt-2 italic">
              If unchecked, you can adjust task dates manually after importing.
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    const parentTasks = tasksToImport.filter((t) => !t.parentTempId);

    return (
      <div className="flex flex-col gap-4">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Remember that you can adjust task dates after importing them to fit
            your project schedule.
          </p>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            Review and optionally edit the tasks before importing. Start date:{' '}
            <span className="font-medium">{startDate}</span>
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
          {parentTasks.map((task) => {
            const subtasks = tasksToImport.filter((t) => t.parentTempId === task.tempId);
            return (
              <div key={task.tempId} className="p-3 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-2">
                  <Input
                    value={task.name}
                    onChange={(e) => handleTaskNameChange(task.tempId, e.target.value)}
                    className="flex-1 text-sm"
                  />
                  {task.daysFromStart !== null && task.daysFromStart !== undefined && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {calculateTaskDate(startDate, task.daysFromStart)?.toLocaleDateString(
                        'en-US',
                        { month: 'short', day: 'numeric' },
                      ) || `Day +${task.daysFromStart}`}
                    </span>
                  )}
                </div>
                {(task.equipments?.length || task.materials?.length) && (
                  <div className="mt-1 flex gap-3 text-xs text-gray-400">
                    {task.equipments && task.equipments.length > 0 && (
                      <span>{task.equipments.length} equipment</span>
                    )}
                    {task.materials && task.materials.length > 0 && (
                      <span>{task.materials.length} material(s)</span>
                    )}
                  </div>
                )}
                {subtasks.length > 0 && (
                  <div className="ml-6 mt-2 space-y-2">
                    {subtasks.map((subtask) => (
                      <div key={subtask.tempId} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">└</span>
                          <Input
                            value={subtask.name}
                            onChange={(e) => handleTaskNameChange(subtask.tempId, e.target.value)}
                            className="flex-1 text-sm"
                          />
                          {subtask.daysFromStart !== null &&
                            subtask.daysFromStart !== undefined && (
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {calculateTaskDate(
                                  startDate,
                                  subtask.daysFromStart,
                                )?.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                }) || `Day +${subtask.daysFromStart}`}
                              </span>
                            )}
                        </div>
                        {(subtask.equipments?.length || subtask.materials?.length) && (
                          <div className="ml-5 flex gap-3 text-xs text-gray-400">
                            {subtask.equipments && subtask.equipments.length > 0 && (
                              <span>{subtask.equipments.length} equipment</span>
                            )}
                            {subtask.materials && subtask.materials.length > 0 && (
                              <span>{subtask.materials.length} material(s)</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={getTitle()}
        confirmText={getConfirmText()}
        handleConfirm={handleConfirm}
        isLoading={importMutation.isPending}
        disableConfirm={isConfirmDisabled()}
        size="large">
        {step !== 'select' && (
          <button type="button"
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        )}

        {step === 'select' && renderSelectStep()}
        {step === 'configure' && renderConfigureStep()}
        {step === 'preview' && renderPreviewStep()}
      </Modal>

      <ConfirmationModal
        isOpen={!!templateToDelete}
        title="Delete Template"
        description={`Are you sure you want to delete "${templateToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onClose={() => setTemplateToDelete(null)}
        onConfirm={handleDeleteTemplate}
      />
    </>
  );
};

export default ImportTaskTemplateModal;
